import { Injectable, computed, inject, signal } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../environments/environment';
import { AnalyticsEligibilityService } from './analytics-eligibility.service';

const REJECTION_KEY = 'sahkaarx.analytics.v2.learner-rejected';
type AnalyticsWindow = Window & {
  [key: `ga-disable-${string}`]: boolean;
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly eligibility = inject(AnalyticsEligibilityService);
  readonly enabled = environment.production && !!environment.ga4MeasurementId;
  readonly adult = signal(false);
  readonly canOfferConsent = computed(() => this.enabled && this.adult());
  readonly showPreferences = signal(false);
  private accepted = false;
  private ready = false;
  private configured = false;
  private script: HTMLScriptElement | null = null;
  private started = false;
  private session = -1;
  private previousPage = '';
  private lastNavigation = -1;
  private readonly win = window as unknown as AnalyticsWindow;

  private get allowed(): boolean { return this.enabled && this.eligibility.current.adult && this.accepted; }

  start(): void {
    if (!this.enabled || this.started) return;
    this.started = true;
    this.block();
    this.eligibility.changes.subscribe(state => {
      if (state.session !== this.session) {
        this.accepted = false;
        this.session = state.session;
      }
      const wasAdult = this.adult();
      this.adult.set(state.adult);
      if (!this.allowed) this.block();
      if (!state.adult) this.showPreferences.set(false);
      else if (!wasAdult || !this.accepted) {
        let rejected = false;
        try { rejected = localStorage.getItem(REJECTION_KEY) === 'rejected'; } catch { /* Optional storage. */ }
        this.showPreferences.set(!this.accepted && !rejected);
        if (this.accepted) this.activate();
      }
    });
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.pageView());
  }

  setConsent(accepted: boolean): void {
    if (accepted && !this.canOfferConsent()) return;
    const wasAccepted = this.accepted;
    this.accepted = accepted;
    this.showPreferences.set(false);
    try {
      if (accepted) localStorage.removeItem(REJECTION_KEY);
      else localStorage.setItem(REJECTION_KEY, 'rejected');
    } catch { /* Acceptance is always in memory, limited to this auth session. */ }
    if (!accepted) this.block();
    else if (!wasAccepted) this.activate();
  }

  private block(): void {
    this.win[`ga-disable-${environment.ga4MeasurementId}`] = true;
    this.win.dataLayer?.splice(0);
    this.configured = false;
    this.previousPage = '';
    this.lastNavigation = -1;
  }

  private tag(...args: unknown[]): void {
    if (this.allowed && this.ready) this.win.gtag?.(...args);
  }

  private activate(): void {
    if (!this.allowed) return;
    if (this.ready) {
      this.configure();
      this.pageView();
      return;
    }
    if (this.script) return;
    this.win.dataLayer = this.win.dataLayer || [];
    const win = this.win;
    win.gtag = function () { win.dataLayer!.push(arguments); };
    this.script = document.createElement('script');
    this.script.async = true;
    this.script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.ga4MeasurementId}`;
    this.script.onload = () => {
      this.ready = true;
      if (!this.allowed) return;
      this.configure();
      this.pageView();
    };
    this.script.onerror = () => { this.script?.remove(); this.script = null; this.block(); };
    document.head.appendChild(this.script);
  }

  private configure(): void {
    if (!this.allowed || !this.ready || this.configured) return;
    this.win[`ga-disable-${environment.ga4MeasurementId}`] = false;
    this.tag('consent', 'default', {
      analytics_storage: 'granted', ad_storage: 'denied',
      ad_user_data: 'denied', ad_personalization: 'denied'
    });
    this.tag('consent', 'update', { analytics_storage: 'granted' });
    this.tag('js', new Date());
    this.tag('config', environment.ga4MeasurementId, {
      send_page_view: false, allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: window.location.origin, page_referrer: '', page_title: 'SahkaarX'
    });
    this.configured = true;
  }

  private pageView(): void {
    if (!this.allowed || !this.ready || !this.configured || !this.router.navigated) return;
    const navigation = this.router.lastSuccessfulNavigation?.id ?? 0;
    if (navigation === this.lastNavigation) return;
    const paths: string[] = [];
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      const path = route.routeConfig?.path;
      if (path) paths.push(path);
      route = route.firstChild;
    }
    const path = '/' + paths.join('/');
    const page = window.location.origin + path;
    this.tag('set', { page_location: page, page_title: path, page_referrer: this.previousPage });
    this.tag('event', 'page_view', { page_location: page, page_title: path, page_referrer: this.previousPage });
    this.previousPage = page;
    this.lastNavigation = navigation;
  }
}
